import React from 'react';
import PageWrapper from '@/components/utils/PageWrapper';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Users, Lightbulb, TrendingUp } from 'lucide-react';
import AnimatedElement from '@/components/utils/AnimatedElement';
import { motion } from 'framer-motion';

const About = () => {
  return (
    <PageWrapper
      title="About Us"
      subtitle="Transforming businesses through innovative AI solutions"
    >
      <div className="container mx-auto px-6 pb-20">
        {/* Our Story Section */}
        <AnimatedElement animation="slideUp" delay={0.1} className="mb-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Our Story</h2>
            <div className="prose prose-lg dark:prose-invert max-w-none">
              <p className="text-muted-foreground leading-relaxed mb-4">
                Fictional Stream was founded in 2025 by engineers who previously built Raemox, 
                a company focused on IoT and automation solutions. After years of working with 
                manufacturing clients across Europe and Asia, we saw a clear need: companies had data, 
                but lacked the expertise to turn it into actionable insights.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Today, we work with clients in 15+ countries. Our team is distributed across time zones, 
                allowing us to provide 24/7 support for critical systems.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                We're not a consultancy that builds PowerPoints. We're engineers who write code, 
                deploy systems, and maintain them in production. Every project starts with a clear 
                question: "What problem are we actually solving?"
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mt-12">
            {[
              {
                icon: <Lightbulb className="h-8 w-8 text-blue-500 mb-3" />,
                title: "Production-First",
                description: "We build systems that run 24/7, not demos."
              },
              {
                icon: <Users className="h-8 w-8 text-primary mb-3" />,
                title: "Global Reach",
                description: "Serving clients across 15+ countries."
              },
              {
                icon: <TrendingUp className="h-8 w-8 text-green-500 mb-3" />,
                title: "Measured Impact",
                description: "Every project tied to business outcomes."
              }
            ].map((item, index) => (
              <motion.div 
                key={index}
                className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-subtle border border-gray-100 dark:border-gray-700 text-center"
                whileHover={{ scale: 1.02, y: -3, transition: { duration: 0.2 } }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex justify-center mb-3">
                  {item.icon}
                </div>
                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </AnimatedElement>
        
        {/* Leadership Team */}
        <AnimatedElement animation="slideUp" delay={0.2} className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Team</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-5xl mx-auto">
            {[
              // {
              //   name: "Dr. Sarah Chen",
              //   role: "Chief Executive Officer",
              //   bio: "Ph.D. in Computer Science with 15+ years in AI research and business leadership."
              // },
              // {
              //   name: "Michael Rodriguez",
              //   role: "Chief Technology Officer",
              //   bio: "Former Google AI researcher with expertise in large language models and computer vision."
              // },
              {
                name: "Vijay S K",
                role: "Co-Founder & Operations Executive",
                bio: "Experienced in AI, ML, and IoT. Ex Co-founder of Raemox",
                image: "/vijay-s-k.png"
              },
              {
                name: "Devaraj S",
                role: "Co-Founder & Technical Executive`",
                bio: "Experienced in Product operations. Ex-Product Designer at Raemox",
                image: "/devaraj-s.png"
              }
            ].map((person, index) => (
              <motion.div 
                key={index} 
                className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-subtle border border-gray-100 dark:border-gray-700 service-card"
                whileHover={{ scale: 1.05, y: -8, transition: { duration: 0.2 } }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="bg-gray-200 dark:bg-gray-700 h-64 flex items-center justify-center">
                  {person.name === "Vijay S K" || person.name === "Devaraj S" ? (
                    <img
                      src={person.image}
                      alt={person.name}
                      className="h-48 w-48 rounded-full object-cover border-4 border-white dark:border-gray-800 shadow-lg"
                    />
                  ) : (
                    <Users className="h-20 w-20 text-gray-400 dark:text-gray-500" />
                  )}
                </div>
                <div className="p-8">
                  <h3 className="text-xl font-bold mb-2">{person.name}</h3>
                  <p className="text-primary/80 mb-3 text-base font-medium">{person.role}</p>
                  <p className="text-muted-foreground text-base leading-relaxed">{person.bio}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </AnimatedElement>
        
        {/* Call to Action */}
        <AnimatedElement animation="slideUp" delay={0.3}>
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold mb-4">Ready to get started?</h2>
            <Link to="/contact" className="glass-button-primary px-8 py-3 text-base font-medium inline-block btn-hover-glow">
              Get in Touch
              <ArrowUpRight className="ml-2 h-4 w-4 inline-block" />
            </Link>
          </div>
        </AnimatedElement>
      </div>
    </PageWrapper>
  );
};

export default About;
